import React, {useEffect, useMemo, useState, useCallback, useRef} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import {
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    TextField,
    ClickAwayListener,
    Dialog,
    Button,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ImageIcon from "@mui/icons-material/Image";
// import {AudioWaveformIcon, VideoCameraIcon} from "../NewHome/ProjectThumbnail";
// import {FilmIcon} from "../LibrettoEditor/FilmIcon";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DownloadIcon from "@mui/icons-material/Download";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {KeyboardArrowDown, KeyboardArrowRight, FolderOutlined} from "@mui/icons-material";
import {observer} from "mobx-react-lite";
// import {ProjectAssetsStore} from "../../state/ProjectAssetsStore";
// import {useAssetStore} from "../../storage/AssetStore";
import {
    handleDownloadButtonClick,
    formatDurationBrief,
    LibrettoProjectTypes,
    IsVideoTrackType,
    ClipSize16And9RatioId,
    ClipSize9And16RatioId,
    ClipSize1And1RatioId,
    ClipSizeToWidthAndHeight
} from "../utils/utils";
// import AgenticUploadContentCard from "../NewHome/AgenticUploadContentCard";
import {ALL_FORMATS, Input, UrlSource} from "mediabunny";
import {AudioWaveformIcon, VideoCameraIcon} from "../pages/NewHome/ProjectThumbnail";
import {useAssetStore} from "../storage/AssetStore";
import {ProjectAssetsStore} from "../state/ProjectAssetsStore";
import {SequenceCompositionService} from "./services/SequenceCompositionService";
import {SequenceStateStore} from "./store/SequenceStateStore";
import {useConfirmDialog} from "../pages/NewHome/DeleteConfirmation";
import {DeleteIcon} from "../pages/NewHome/HomeIcons";
import {ContextMenuStore} from "../librettoEditor/store/ContextMenuStore";
import {VideoClip} from "../engine/clips/video/video";
import {AudioClip} from "../engine/clips";
import {pickFilesAndUpload} from "../utils/multiFileUpload";
import {validateFilesForUpload} from "../utils/fileValidation";
import {UnsupportedFilesDialog, UnsupportedFile} from "../components/UnsupportedFilesDialog";
import {FilmIcon} from "../pages/ModernEditor/FilmIcon";
import {MediaSourceRef} from "../engine/sources/media-source";
import {AuthContext} from "../context/AuthContext";
import {CreateClipForComposition} from "../pages/V2Editor/utils";
import {GetTracksLocalAvailability} from "../storage/LocalAssetStore";
import { alpha, useTheme } from "@mui/material/styles";

interface MediaItem {
    id: string;
    title: string;
    thumbnailUrl?: string;
    duration?: number;
    type: 'image' | 'video' | 'audio';
    status: string;
    progress?: number;
    track?: any;
    record?: any;
}

// Backup Promotion Dialog Component
interface BackupPromotionDialogProps {
    isOpen: boolean;
    onClose: () => void;
    trackTitle: string;
    backupTrackId: string;
    onPromote: (backupTrackId: string) => Promise<void>;
}

const BackupPromotionDialog: React.FC<BackupPromotionDialogProps> = ({
    isOpen,
    onClose,
    trackTitle,
    backupTrackId,
    onPromote,
}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const subtleHoverBackground = isDarkMode
        ? alpha(theme.palette.common.white, 0.06)
        : alpha(theme.palette.common.black, 0.04);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handlePromote = async () => {
        setIsLoading(true);
        try {
            await onPromote(backupTrackId);
            onClose();
        } finally {
            setIsLoading(false);
        }
    };

    const displayTitle = trackTitle.length > 40 
        ? trackTitle.substring(0, 40) + '...' 
        : trackTitle;

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '420px',
                    padding: '20px 24px',
                    borderRadius: '12px',
                    boxShadow: '0px 20px 60px rgba(15, 23, 42, 0.08)',
                }
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: '#FEF3C7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <InfoOutlinedIcon sx={{ fontSize: 18, color: '#F59E0B' }} />
                    </Box>
                    <Typography sx={{ 
                        fontFamily: 'Inter',
                        fontSize: '15px',
                        fontWeight: 500,
                        lineHeight: '22px',
                        color: 'var(--app-text-primary)',
                    }}>
                        Recording incomplete
                    </Typography>
                </Box>
                <IconButton 
                    size="small" 
                    onClick={onClose}
                    sx={{ 
                        color: 'var(--app-text-subtle)',
                        padding: '4px',
                        mt: -0.5,
                        mr: -1,
                        '&:hover': { backgroundColor: subtleHoverBackground }
                    }}
                >
                    <Box component="span" sx={{ fontSize: '18px', lineHeight: 1 }}>×</Box>
                </IconButton>
            </Box>
            
            <Typography sx={{ 
                fontFamily: 'Inter',
                fontSize: '13px',
                fontWeight: 400,
                color: 'var(--app-text-muted)',
                mb: 1,
                lineHeight: 1.5,
            }}>
                The local recording for <strong style={{ color: 'var(--app-text-primary)' }}>{displayTitle}</strong> appears to be incomplete.
            </Typography>
            
            <Typography sx={{ 
                fontFamily: 'Inter',
                fontSize: '13px',
                fontWeight: 400,
                color: 'var(--app-text-muted)',
                mb: 2,
                lineHeight: 1.5,
            }}>
                A backup recording from the server is available. While slightly lower quality, it can be used to recover this track.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                    onClick={onClose}
                    sx={{
                        flex: 1,
                        fontFamily: 'Inter',
                        fontSize: '13px',
                        fontWeight: 500,
                        textTransform: 'none',
                        color: 'var(--app-text-muted)',
                        backgroundColor: 'var(--app-bg-subtle)',
                        borderRadius: '999px',
                        py: 1,
                        '&:hover': { backgroundColor: 'var(--app-bg-subtle)', opacity: 0.9 },
                    }}
                >
                    Keep waiting
                </Button>
                <Button
                    onClick={handlePromote}
                    disabled={isLoading}
                    sx={{
                        flex: 1,
                        fontFamily: 'Inter',
                        fontSize: '13px',
                        fontWeight: 500,
                        textTransform: 'none',
                        color: '#FFFFFF',
                        backgroundColor: '#F59E0B',
                        borderRadius: '999px',
                        py: 1,
                        '&:hover': { backgroundColor: '#D97706' },
                        '&.Mui-disabled': { backgroundColor: '#FDE68A', color: '#FFFFFF' }
                    }}
                >
                    {isLoading ? 'Promoting...' : 'Use backup'}
                </Button>
            </Box>
        </Dialog>
    );
};

const PaperPanel: React.FC<{
    title: string;
    onAdd?: () => void;
    children: React.ReactNode;
    fill?: boolean; // when true, fills remaining column height
}> = ({title, onAdd, children, fill}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--app-bg-surface)',
                border: '0.5px solid var(--app-border-subtle)',
                borderRadius: '12px',
                px: 1,
                py: 1,
                ...(fill ? {flex: 1, minHeight: 0} : {}),
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 0.5,
                    py: 0.5,
                }}
            >
                <Typography sx={{fontSize: 14, fontWeight: 600, userSelect: 'none', color: 'var(--app-text-primary)'}}>{title}</Typography>
                <IconButton size="small" onClick={onAdd} sx={{ color: 'var(--app-icon-primary)' }}>
                    <AddIcon fontSize="small"/>
                </IconButton>
            </Box>

            <Divider sx={{my: 0.5, borderColor: 'var(--app-border-subtle)'}}/>

            <Box sx={{...(fill ? {overflow: 'auto'} : {})}}>
                {children}
            </Box>
        </Box>
    );
};

// Reusable row (shows 3-dots on hover like MediaPanelContainer)
const RowItem: React.FC<{
    icon: React.ReactNode;
    primary: string;
    disabled?: boolean;
    onClick?: () => void;
    onMenu?: (e: React.MouseEvent) => void;
}> = ({icon, primary, disabled, onClick, onMenu}) => {
    const theme = useTheme();
    const hoverBackground = theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.06)
        : alpha(theme.palette.common.black, 0.04);
    return (
        <ListItem
            disablePadding
            sx={{
                '&:hover': {
                    backgroundColor: hoverBackground,
                    borderRadius: '6px',
                },
                // reveal action button on hover
                '&:hover .row-actions': {
                    opacity: 1,
                    pointerEvents: 'auto',
                },
            }}
            secondaryAction={
                <IconButton
                    size="small"
                    className="row-actions"
                    onClick={onMenu}
                    sx={{opacity: 0, transition: 'opacity 0.2s ease'}}
                >
                    <MoreVertIcon fontSize="small"/>
                </IconButton>
            }
        >
            <ListItemButton onClick={onClick} disabled={disabled} sx={{py: 0.75, pr: 4}}>
                <ListItemIcon sx={{minWidth: 32, mr: 1}}>
                    <Box
                        sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '4px',
                            background: 'var(--app-bg-subtle)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                        }}
                    >
                        {icon}
                    </Box>
                </ListItemIcon>
                <ListItemText
                    primaryTypographyProps={{
                        fontSize: 13,
                        fontWeight: 400,
                        noWrap: true,
                    }}
                    primary={primary}
                />
            </ListItemButton>
        </ListItem>
    );
};

// @ts-ignore
export const SequenceMediaSidebar: React.FC = observer(({onClose}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const {GetThumbnailUrl, GetWaveformData} = useAssetStore();
    const {DeleteAsset, DeleteTrack, RenameTrackOrAsset} = useAssetStore();
    const {GetTrackUrl, GetAssetUrl, ResolveAssetUrl} = useAssetStore();
    const {UploadAsset, UploadNonLocalFirstAsset, PromoteBackupTrack} = useAssetStore();
    const authContext = React.useContext(AuthContext);
    const [thumbs, setThumbs] = useState<Record<string, string | undefined>>({});
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [menuItem, setMenuItem] = useState<any | null>(null);
    const [renamingItemId, setRenamingItemId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState("");
    // Unsupported files dialog state
    const [unsupportedFilesDialogOpen, setUnsupportedFilesDialogOpen] = useState<boolean>(false);
    const [unsupportedFiles, setUnsupportedFiles] = useState<UnsupportedFile[]>([]);
    const [processableFiles, setProcessableFiles] = useState<UnsupportedFile[]>([]);
    const processableRawFilesRef = useRef<File[]>([]);
    // Track local availability for tracks (trackId -> isLocallyAvailable)
    const [localAvailability, setLocalAvailability] = useState<Map<string, boolean>>(new Map());
    // Backup promotion dialog state
    const [backupDialogOpen, setBackupDialogOpen] = useState<boolean>(false);
    const [backupTrackInfo, setBackupTrackInfo] = useState<{ title: string; backupTrackId: string } | null>(null);

    const images = ProjectAssetsStore.projectImages;
    const tracks = ProjectAssetsStore.projectTracks;
    const folders = ProjectAssetsStore.projectFolders;
    const rowHoverBackground = isDarkMode
        ? alpha(theme.palette.common.white, 0.06)
        : alpha(theme.palette.common.black, 0.04);
    const thumbnailWellBackground = "var(--app-bg-subtle)";
    const disabledThumbnailOverlay = isDarkMode
        ? alpha(theme.palette.common.black, 0.46)
        : alpha(theme.palette.common.white, 0.7);
    const iconPrimaryColor = "var(--app-icon-primary)";
    const renameInputSx = {
        flex: 1,
        '& .MuiInput-input': {
            fontSize: '13px',
            padding: '0px',
            color: 'var(--app-text-primary)',
        },
        '& .MuiInput-underline:before': {
            borderBottomColor: 'var(--app-border-subtle)',
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottomColor: 'var(--app-border-muted)',
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: 'var(--app-text-primary)',
        },
    };

    // Track which folders are expanded (by folderId)
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

    const {openDialog, ConfirmDialogComponent} = useConfirmDialog({confirmText: 'Delete', cancelText: 'Cancel'});

    const files = [
        ...images.map((img: any) => ({
            id: String(img.id),
            name: img.title || img.name || img.fileName || img.originalName || String(img.id),
            type: 'image' as const,
            thumbKey: img.thumbnailId as string | undefined,
            record: img,
        })),
        ...tracks.map((tr: any) => {
            const isVideoTrack = IsVideoTrackType(tr.trackType);
            const type = isVideoTrack ? 'video' : 'audio';
            return {
                id: String(tr.id),
                name: tr.title || tr.filename || tr.fileName || tr.originalName || String(tr.id),
                type,
                // for tracks, prefer hasThumbnail flag; thumbnails are fetched via trackId
                thumbKey: undefined,
                record: tr,
            } as const;
        }),
    ];

    console.log('[SequenceMediaSidebar] counts', {
        images: images?.length,
        tracks: tracks?.length,
        files: files?.length,
    });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const updates: Record<string, string | undefined> = {};
            for (const f of files) {
                if (thumbs[f.id] != null) continue;
                try {
                    if (f.type === 'audio') {
                        // never fetch thumbnails for audio
                        continue;
                    }
                    if (f.type === 'video') {
                        if (!f.record?.hasThumbnail) continue;
                        const url = await GetThumbnailUrl({thumbnailId: f.id, projectId: ProjectAssetsStore.projectId as string});
                        if (!cancelled) updates[f.id] = url;
                        continue;
                    }
                    if (f.type === 'image') {
                        if (!f.thumbKey) continue; // requires thumbnailId
                        const url = await GetThumbnailUrl({thumbnailId: f.thumbKey, projectId: ProjectAssetsStore.projectId as string});
                        if (!cancelled) updates[f.id] = url;
                        continue;
                    }
                } catch {
                }
            }
            if (!cancelled && Object.keys(updates).length) {
                setThumbs(prev => ({...prev, ...updates}));
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);

    // Check local availability for all tracks (for local-first uploads)
    useEffect(() => {
        let cancelled = false;
        const trackIds = tracks.map((tr: any) => tr.id || tr.trackId).filter(Boolean);
        if (trackIds.length === 0) {
            setLocalAvailability(new Map());
            return;
        }
        
        (async () => {
            const availability = await GetTracksLocalAvailability(trackIds);
            if (!cancelled) {
                setLocalAvailability(availability);
            }
        })();
        
        return () => { cancelled = true; };
    }, [tracks]);

    /**
     * Determine if a file (track) is ready to use.
     */
    const isFileReady = (file: { id: string; type: string; record?: any }): boolean => {
        if (file.type === 'image') return true;
        const trackId = file.id;
        const trackRecord = file.record;
        const status = trackRecord?.status;
        if (status && status.toLowerCase() === 'ready') return true;
        if (localAvailability.get(trackId)) return true;
        return false;
    };

    /**
     * Get a human-readable status label for a non-ready track.
     */
    const getFileStatusLabel = (file: { id: string; type: string; record?: any }): string | null => {
        if (file.type === 'image') return null;
        if (isFileReady(file)) return null;
        const status = file.record?.status;
        if (!status) return 'Processing';
        const statusLower = status.toLowerCase();
        if (statusLower === 'processing...' || statusLower === 'processing') return 'Processing';
        if (statusLower === 'uploading...' || statusLower === 'uploading') return 'Uploading';
        if (statusLower === 'recording in progress' || statusLower === 'recording') return 'Recording';
        if (statusLower === 'ready') return null;
        return status;
    };

    /**
     * Get transcription status info for a ready track.
     */
    const getTranscriptionStatus = (file: { id: string; type: string; record?: any }): { label: string; isError: boolean; isSilent?: boolean } | null => {
        if (file.type === 'image') return null;
        if (!isFileReady(file)) return null;
        const transcriptionState = file.record?.transcriptionState;
        if (!transcriptionState) return null;
        const stateLower = transcriptionState.toLowerCase();
        if (stateLower === 'queued' || stateLower === 'processing' || stateLower === 'client_extracting' || stateLower === 'pending') {
            return { label: 'Transcribing', isError: false };
        }
        if (stateLower === 'failed') {
            return { label: 'Transcription failed', isError: true };
        }
        if (stateLower === 'silent' || stateLower === 'not_needed') {
            return { label: 'Silent - no speech detected', isError: false, isSilent: true };
        }
        return null;
    };

    /**
     * Check if a track has a backup available that can be promoted.
     */
    const getBackupAvailableInfo = (file: { id: string; type: string; record?: any }): { backupTrackId: string } | null => {
        if (file.type === 'image') return null;
        const trackRecord = file.record;
        if (!trackRecord) return null;
        const status = String(trackRecord.status || '').toLowerCase();
        const backupTrackId = trackRecord.backupTrackId;
        const backupAvailableAt = trackRecord.backupAvailableAt;
        if (
            status === 'recording' &&
            backupTrackId &&
            backupTrackId !== '' &&
            backupAvailableAt !== undefined &&
            Date.now() >= backupAvailableAt
        ) {
            return { backupTrackId };
        }
        return null;
    };

    console.log("Files: ", files);

    // Toggle folder expand/collapse
    const toggleFolder = (folderId: string) => {
        setExpandedFolders(prev => {
            const next = new Set(prev);
            if (next.has(folderId)) {
                next.delete(folderId);
            } else {
                next.add(folderId);
            }
            return next;
        });
    };

    // Get files that belong to a specific folder
    const getFilesInFolder = (folderId: string) => {
        const folder = folders.find((f: any) => f.id === folderId);
        const fileIds = folder?.fileIds || [];
        return files.filter(f => fileIds.includes(f.id));
    };

    // Get files that don't belong to any folder (unfiled)
    const getUnfiledFiles = useMemo(() => {
        const allFolderFileIds = new Set<string>();
        for (const folder of folders) {
            for (const fileId of (folder.fileIds || [])) {
                allFolderFileIds.add(fileId);
            }
        }
        return files.filter(f => !allFolderFileIds.has(f.id));
    }, [files, folders]);

    const fileIconFor = (t: string) => {
        switch (t) {
            case 'image':
                return <ImageIcon sx={{fontSize: 18, color: iconPrimaryColor}}/>;
            case 'audio':
                return <AudioWaveformIcon sx={{fontSize: 18, color: iconPrimaryColor}}/>;
            case 'video':
                return <VideoCameraIcon backgroundColor={thumbnailWellBackground}/>;
            default:
                return <AudioWaveformIcon sx={{fontSize: 18, color: iconPrimaryColor}}/>;
        }
    };

    const titleTextStyle = {
        color: '#1F2937',              // text-gray-800
        paddingLeft: '8px',            // px-2
        paddingRight: '8px',
        flex: 1,                       // flex-1
        overflow: 'hidden',            // overflow-hidden
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: '14px',              // body-2-medium (typically ~14px)
        fontWeight: 500,               // medium
        lineHeight: '20px',            // matches Tailwind body line-height
        fontFamily: 'Inter, sans-serif',
        userSelect: 'none',
    };

    const startRenaming = (item: MediaItem) => {
        setRenamingItemId(item.id);
        setRenameValue(item.title);
    };

    const handleContextMenuClick = (event: React.MouseEvent, item: MediaItem, isReady: boolean = true) => {
        event.preventDefault();
        event.stopPropagation();

        // For non-ready tracks, only show Rename and Delete options
        if (!isReady) {
            const limitedMenuItems = [
                {
                    title: "Rename",
                    icon: () => <ModeEditIcon/>,
                    onClick: () => {
                        startRenaming(item);
                        ContextMenuStore.hide();
                    },
                },
                {
                    title: "Delete",
                    icon: () => <DeleteIcon/>,
                    onClick: () => {
                        openDialog({
                            title: `Delete this ${item.type}?`,
                            message: 'It will be permanently deleted.',
                            onConfirm: async () => {
                                if (item.type === 'image') {
                                    await DeleteAsset({assetId: item.id});
                                } else {
                                    await DeleteTrack({trackId: item.id});
                                }
                            },
                        });
                        ContextMenuStore.hide();
                    },
                },
            ];
            ContextMenuStore.show(event.clientX, event.clientY, limitedMenuItems);
            return;
        }

        const menuItems = [
            {
                title: "Rename",
                icon: () => <ModeEditIcon/>,
                onClick: () => {
                    startRenaming(item);
                    ContextMenuStore.hide();
                },
            },
            {
                title: "Delete",
                icon: () => <DeleteIcon/>,
                onClick: () => {
                    openDialog({
                        title: `Delete this ${item.type}?`,
                        message: 'It will be permanently deleted.',
                        onConfirm: async () => {
                            if (item.type === 'image') {
                                await DeleteAsset({assetId: item.id});
                            } else {
                                await DeleteTrack({trackId: item.id});
                            }
                        },
                    });
                    ContextMenuStore.hide();
                },
            },
            {
                title: "Download",
                icon: () => <DownloadIcon/>,
                onClick: async () => {
                    console.log('Download:', item);
                    await handleDownloadButtonClick({
                        assetTitle: "",
                        trackTitle: item.track?.title || item.title,
                        customTitle: item.track?.title || item.title,
                        isRecording: false,
                        trackUrl: item.track?.objectUrl,
                        trackCreatedAt: null
                    });
                    ContextMenuStore.hide();
                },
            },
        ];

        ContextMenuStore.show(event.clientX, event.clientY, menuItems);
    };

    const openBackupDialog = (trackTitle: string, backupTrackId: string) => {
        setBackupTrackInfo({ title: trackTitle, backupTrackId });
        setBackupDialogOpen(true);
    };

    const handlePromoteBackup = useCallback(async (backupTrackId: string) => {
        const success = await PromoteBackupTrack({ backupTrackId });
        if (success) {
            console.log('[SequenceMediaSidebar] Backup promotion initiated for backup track:', backupTrackId);
        }
    }, [PromoteBackupTrack]);

    const handleAddToComposition = async (f: any) => {
        console.log("adding media of type: ", f.type);

        const clip = await CreateClipForComposition({type: f.type as string, assetId: f.id, assetTitle: f?.record?.title, assetResolution: f?.record?.resolution || "", composition: SequenceCompositionService.composition, stateStore: SequenceStateStore, resolveAssetUrl: ResolveAssetUrl});
        if (!clip) return;
        await SequenceCompositionService.composition.add(clip);
    };

    const handleClickAway = () => {
        console.log("Clicked away");
        onClose();
    }

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box sx={{height: '100%', width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', p: '3px'}}>
                <ConfirmDialogComponent/>
                <BackupPromotionDialog
                    isOpen={backupDialogOpen}
                    onClose={() => setBackupDialogOpen(false)}
                    trackTitle={backupTrackInfo?.title || ''}
                    backupTrackId={backupTrackInfo?.backupTrackId || ''}
                    onPromote={handlePromoteBackup}
                />
                {/*<Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start', marginLeft: '8px'}}>*/}
                {/*    <Box sx={{width: '24px', height: '24px'}}>*/}
                {/*        <FilmIcon/>*/}
                {/*    </Box>*/}
                {/*    <Box sx={titleTextStyle}>Media files</Box>*/}
                {/*</Box>*/}
                <PaperPanel
                    title="Media files"
                    onAdd={() => {
                        // Use file picker to select files, then validate and upload
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'video/*,audio/*,image/*';
                        input.multiple = true;
                        input.onchange = async () => {
                            const files = Array.from(input.files ?? []);
                            if (files.length === 0) return;
                            
                            const { localOkFiles, serverProcessFiles, rejectedFiles } = await validateFilesForUpload(files);

                            const reviewProcessable = serverProcessFiles.map((f) => f.display);
                            const shouldShowReview = reviewProcessable.length > 0 || rejectedFiles.length > 0;
                            if (shouldShowReview) {
                                setUnsupportedFiles(rejectedFiles);
                                setProcessableFiles(reviewProcessable);
                                processableRawFilesRef.current = serverProcessFiles.map((f) => f.file);
                                setUnsupportedFilesDialogOpen(true);
                            }

                            // Upload local-first supported files
                            for (const file of localOkFiles) {
                                await UploadAsset({
                                    projectId: ProjectAssetsStore.projectId as string,
                                    projectType: LibrettoProjectTypes.ProjectTypeEditor,
                                    file,
                                    filetype: file.type ? (file.type.startsWith('image/') ? "ImageUpload" : (file.type.startsWith('video/') ? "video" : "audio")) : "audio",
                                    languageCode: "auto",
                                });
                            }
                            
                            input.remove();
                        };
                        input.click();
                    }}
                    fill
                >
                    <List dense sx={{py: 0}}>
                        {/* Folders and their contents */}
                        {folders.map((folder: any) => {
                            const folderId = folder.id;
                            const folderName = folder.name || "Untitled Folder";
                            const isExpanded = expandedFolders.has(folderId);
                            const filesInFolder = getFilesInFolder(folderId);

                            return (
                                <React.Fragment key={folderId}>
                                    {/* Folder row */}
                                    <ListItem
                                        disablePadding
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: rowHoverBackground,
                                                borderRadius: '6px',
                                            },
                                        }}
                                    >
                                        <ListItemButton onClick={() => toggleFolder(folderId)} sx={{ py: 0.75, pr: 4 }}>
                                            <ListItemIcon sx={{ minWidth: 24, mr: 0.5 }}>
                                                {isExpanded ? <KeyboardArrowDown sx={{ fontSize: 18 }} /> : <KeyboardArrowRight sx={{ fontSize: 18 }} />}
                                            </ListItemIcon>
                                            <ListItemIcon sx={{ minWidth: 32, mr: 1 }}>
                                                <FolderOutlined sx={{ fontSize: 20, color: iconPrimaryColor }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primaryTypographyProps={{ fontSize: 13, fontWeight: 500, noWrap: true }}
                                                primary={folderName}
                                            />
                                        </ListItemButton>
                                    </ListItem>

                                    {/* Files inside folder (when expanded) */}
                                    {isExpanded && filesInFolder.map((f) => {
                            const thumbUrl = thumbs[f.id];
                            const isTrack = f.type === 'audio' || f.type === 'video';
                            const durationSec = isTrack ? (f.record?.duration ?? f.record?.Duration ?? undefined) : undefined;
                            const durationStr = durationSec ? formatDurationBrief(durationSec) : undefined;
                                        const fileReady = isFileReady(f);
                                        const statusLabel = getFileStatusLabel(f);
                                        const transcriptionInfo = getTranscriptionStatus(f);
                                        const backupAvailableInfo = getBackupAvailableInfo(f);

                            if (renamingItemId === f.id) {
                                return (
                                    <ListItem key={f.id} disablePadding>
                                                    <ListItemButton sx={{py: 0.75, pr: 4, pl: 5}} onClick={(e) => e.stopPropagation()}>
                                            <ListItemIcon sx={{minWidth: 32, mr: 1}}>
                                                <Box sx={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: '4px',
                                                    background: thumbnailWellBackground,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    overflow: 'hidden'
                                                }}>
                                                    {thumbUrl ? (
                                                        <img src={thumbUrl} alt="thumb"
                                                             style={{width: 28, height: 28, objectFit: 'cover'}}/>
                                                    ) : fileIconFor(f.type)}
                                                </Box>
                                            </ListItemIcon>
                                            <TextField
                                                value={renameValue}
                                                onChange={(e) => setRenameValue(e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                onBlur={() => {
                                                                (async () => {
                                                                    const newName = renameValue.trim();
                                                                    if (newName && newName !== f.name) {
                                                                        try { await RenameTrackOrAsset({ newName, entityId: f.id, isTrack: f.type !== 'image' }); } catch {}
                                                    }
                                                    setRenamingItemId(null);
                                                                })();
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                                    (async () => {
                                                                        const newName = renameValue.trim();
                                                                        if (newName && newName !== f.name) {
                                                                            try { await RenameTrackOrAsset({ newName, entityId: f.id, isTrack: f.type !== 'image' }); } catch {}
                                                        }
                                                        setRenamingItemId(null);
                                                                    })();
                                                    } else if (e.key === 'Escape') {
                                                        setRenamingItemId(null);
                                                        setRenameValue("");
                                                    }
                                                }}
                                                autoFocus
                                                size="small"
                                                variant="standard"
                                                sx={renameInputSx}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            }

                            const primaryNode = (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '13px',
                                            fontWeight: 400,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                                        flex: 1,
                                                        color: fileReady ? 'text.primary' : 'text.disabled',
                                        }}
                                    >
                                        {f.name}
                                    </Typography>
                                                {!fileReady && statusLabel && !backupAvailableInfo && (
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            fontSize: '10px', 
                                                            color: '#F2880C', 
                                                            flexShrink: 0,
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {statusLabel}
                                                    </Typography>
                                                )}
                                                {backupAvailableInfo && (
                                                    <Tooltip title="Recording incomplete - click to use backup" arrow placement="top">
                                                        <Box 
                                                            component="span"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                openBackupDialog(f.name, backupAvailableInfo.backupTrackId);
                                                            }}
                                                            sx={{ 
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flexShrink: 0,
                                                                cursor: 'pointer',
                                                                pointerEvents: 'auto',
                                                                width: 18,
                                                                height: 18,
                                                                borderRadius: '50%',
                                                                backgroundColor: '#FEF3C7',
                                                                border: '1px solid #FDE68A',
                                                                '&:hover': {
                                                                    backgroundColor: '#FDE68A',
                                                                }
                                                            }}
                                                        >
                                                            <InfoOutlinedIcon sx={{ fontSize: 12, color: '#F59E0B' }} />
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                                {fileReady && durationStr && (
                                        <Typography variant="caption"
                                                    sx={{fontSize: '11px', color: 'text.secondary', flexShrink: 0}}>
                                            {durationStr}
                                        </Typography>
                                    )}
                                </Box>
                            );

                            return (
                                            <ListItem
                                    key={f.id}
                                                disablePadding
                                                sx={{
                                                    '&:hover': {
                                                        backgroundColor: fileReady ? rowHoverBackground : 'transparent',
                                                        borderRadius: '6px',
                                                    },
                                                    '&:hover .row-actions': {
                                                        opacity: 1,
                                                        pointerEvents: 'auto',
                                                    },
                                                }}
                                                secondaryAction={
                                                    <IconButton
                                                        size="small"
                                                        className="row-actions"
                                                        onClick={(e) => handleContextMenuClick(e, {...f, title: f.name, status: fileReady ? "ready" : "processing"}, fileReady)}
                                                        sx={{opacity: 0, transition: 'opacity 0.2s ease'}}
                                                    >
                                                        <MoreVertIcon fontSize="small"/>
                                                    </IconButton>
                                                }
                                            >
                                                <ListItemButton 
                                                    onClick={() => { if (fileReady) void handleAddToComposition(f); }} 
                                                    disabled={!fileReady}
                                                    sx={{
                                                        py: 0.75, 
                                                        pr: 4,
                                                        pl: 5,
                                                        '&.Mui-disabled': {
                                                            opacity: 1,
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon sx={{minWidth: 32, mr: 1}}>
                                                        <Box sx={{
                                                            width: 28,
                                                            height: 28,
                                                            borderRadius: '4px',
                                                            background: thumbnailWellBackground,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            overflow: 'hidden',
                                                            position: 'relative',
                                                        }}>
                                                            {thumbUrl ? (
                                                                <img src={thumbUrl} alt="thumb"
                                                                     style={{width: 28, height: 28, objectFit: 'cover', opacity: fileReady ? 1 : 0.5}}/>
                                                            ) : fileIconFor(f.type)}
                                                            {!fileReady && !backupAvailableInfo && (
                                                                <Box sx={{ 
                                                                    position: 'absolute', 
                                                                    inset: 0, 
                                                                    display: 'flex', 
                                                                    alignItems: 'center', 
                                                                    justifyContent: 'center',
                                                                    backgroundColor: disabledThumbnailOverlay,
                                                                }}>
                                                                    <CircularProgress size={14} thickness={5} sx={{ color: '#F2880C' }} />
                                                                </Box>
                                                            )}
                                                            {transcriptionInfo && (
                                                                <Tooltip title={transcriptionInfo.label} arrow placement="top">
                                                                    <Box sx={{ 
                                                                        position: 'absolute', 
                                                                        bottom: -2, 
                                                                        right: -2, 
                                                                        width: 14, 
                                                                        height: 14, 
                                                                        borderRadius: '3px',
                                                                        backgroundColor: transcriptionInfo.isError ? '#FEE2E2' : transcriptionInfo.isSilent ? '#F3F4F6' : '#E0F2FE',
                                                                        display: 'flex', 
                                                                        alignItems: 'center', 
                                                                        justifyContent: 'center',
                                                                        border: '1px solid',
                                                                        borderColor: transcriptionInfo.isError ? '#FECACA' : transcriptionInfo.isSilent ? '#D1D5DB' : '#BAE6FD',
                                                                    }}>
                                                                        {transcriptionInfo.isSilent ? (
                                                                            <Box component="span" sx={{ fontSize: 9, color: '#6B7280', fontWeight: 600, lineHeight: 1 }}>∅</Box>
                                                                        ) : (
                                                                            <FormatQuoteIcon sx={{ 
                                                                                fontSize: 9, 
                                                                                color: transcriptionInfo.isError ? '#DC2626' : '#0284C7',
                                                                            }} />
                                                                        )}
                                                                    </Box>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primaryTypographyProps={{
                                                            fontSize: 13,
                                                            fontWeight: 400,
                                                            noWrap: true,
                                                        }}
                                                        primary={primaryNode}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}

                        {/* Unfiled files (not in any folder) */}
                        {getUnfiledFiles.map((f) => {
                            const thumbUrl = thumbs[f.id];
                            const isTrack = f.type === 'audio' || f.type === 'video';
                            const durationSec = isTrack ? (f.record?.duration ?? f.record?.Duration ?? undefined) : undefined;
                            const durationStr = durationSec ? formatDurationBrief(durationSec) : undefined;
                            const fileReady = isFileReady(f);
                            const statusLabel = getFileStatusLabel(f);
                            const transcriptionInfo = getTranscriptionStatus(f);
                            const backupAvailableInfo = getBackupAvailableInfo(f);

                            if (renamingItemId === f.id) {
                                return (
                                    <ListItem key={f.id} disablePadding>
                                        <ListItemButton sx={{py: 0.75, pr: 4}} onClick={(e) => e.stopPropagation()}>
                                            <ListItemIcon sx={{minWidth: 32, mr: 1}}>
                                                <Box sx={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: '4px',
                                                    background: thumbnailWellBackground,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    overflow: 'hidden'
                                                }}>
                                                    {thumbUrl ? (
                                        <img src={thumbUrl} alt="thumb"
                                             style={{width: 28, height: 28, objectFit: 'cover'}}/>
                                    ) : fileIconFor(f.type)}
                                                </Box>
                                            </ListItemIcon>
                                            <TextField
                                                value={renameValue}
                                                onChange={(e) => setRenameValue(e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                onBlur={() => {
                                                    (async () => {
                                                        const newName = renameValue.trim();
                                                        if (newName && newName !== f.name) {
                                                            try { await RenameTrackOrAsset({ newName, entityId: f.id, isTrack: f.type !== 'image' }); } catch {}
                                                        }
                                                        setRenamingItemId(null);
                                                    })();
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        (async () => {
                                                            const newName = renameValue.trim();
                                                            if (newName && newName !== f.name) {
                                                                try { await RenameTrackOrAsset({ newName, entityId: f.id, isTrack: f.type !== 'image' }); } catch {}
                                                            }
                                                            setRenamingItemId(null);
                                                        })();
                                                    } else if (e.key === 'Escape') {
                                                        setRenamingItemId(null);
                                                        setRenameValue("");
                                                    }
                                                }}
                                                autoFocus
                                                size="small"
                                                variant="standard"
                                                sx={renameInputSx}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            }

                            const primaryNode = (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '13px',
                                            fontWeight: 400,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flex: 1,
                                            color: fileReady ? 'text.primary' : 'text.disabled',
                                        }}
                                    >
                                        {f.name}
                                    </Typography>
                                    {!fileReady && statusLabel && !backupAvailableInfo && (
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                fontSize: '10px', 
                                                color: '#F2880C', 
                                                flexShrink: 0,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {statusLabel}
                                        </Typography>
                                    )}
                                    {backupAvailableInfo && (
                                        <Tooltip title="Recording incomplete - click to use backup" arrow placement="top">
                                            <Box 
                                                component="span"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    openBackupDialog(f.name, backupAvailableInfo.backupTrackId);
                                                }}
                                                sx={{ 
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    cursor: 'pointer',
                                                    pointerEvents: 'auto',
                                                    width: 18,
                                                    height: 18,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#FEF3C7',
                                                    border: '1px solid #FDE68A',
                                                    '&:hover': {
                                                        backgroundColor: '#FDE68A',
                                                    }
                                                }}
                                            >
                                                <InfoOutlinedIcon sx={{ fontSize: 12, color: '#F59E0B' }} />
                                            </Box>
                                        </Tooltip>
                                    )}
                                    {fileReady && durationStr && (
                                        <Typography variant="caption"
                                                    sx={{fontSize: '11px', color: 'text.secondary', flexShrink: 0}}>
                                            {durationStr}
                                        </Typography>
                                    )}
                                </Box>
                            );

                            return (
                                <ListItem
                                    key={f.id}
                                    disablePadding
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: fileReady ? rowHoverBackground : 'transparent',
                                            borderRadius: '6px',
                                        },
                                        '&:hover .row-actions': {
                                            opacity: 1,
                                            pointerEvents: 'auto',
                                        },
                                    }}
                                    secondaryAction={
                                        <IconButton
                                            size="small"
                                            className="row-actions"
                                            onClick={(e) => handleContextMenuClick(e, {...f, title: f.name, status: fileReady ? "ready" : "processing"}, fileReady)}
                                            sx={{opacity: 0, transition: 'opacity 0.2s ease'}}
                                        >
                                            <MoreVertIcon fontSize="small"/>
                                        </IconButton>
                                    }
                                >
                                    <ListItemButton 
                                        onClick={() => { if (fileReady) void handleAddToComposition(f); }} 
                                        disabled={!fileReady}
                                        sx={{
                                            py: 0.75, 
                                            pr: 4,
                                            '&.Mui-disabled': {
                                                opacity: 1,
                                            }
                                        }}
                                    >
                                        <ListItemIcon sx={{minWidth: 32, mr: 1}}>
                                            <Box sx={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: '4px',
                                                background: thumbnailWellBackground,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                overflow: 'hidden',
                                                position: 'relative',
                                            }}>
                                                {thumbUrl ? (
                                                    <img src={thumbUrl} alt="thumb"
                                                         style={{width: 28, height: 28, objectFit: 'cover', opacity: fileReady ? 1 : 0.5}}/>
                                                ) : fileIconFor(f.type)}
                                                {!fileReady && !backupAvailableInfo && (
                                                    <Box sx={{ 
                                                        position: 'absolute', 
                                                        inset: 0, 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center',
                                                        backgroundColor: disabledThumbnailOverlay,
                                                    }}>
                                                        <CircularProgress size={14} thickness={5} sx={{ color: '#F2880C' }} />
                                                    </Box>
                                                )}
                                                {transcriptionInfo && (
                                                    <Tooltip title={transcriptionInfo.label} arrow placement="top">
                                                        <Box sx={{ 
                                                            position: 'absolute', 
                                                            bottom: -2, 
                                                            right: -2, 
                                                            width: 14, 
                                                            height: 14, 
                                                            borderRadius: '3px',
                                                            backgroundColor: transcriptionInfo.isError ? '#FEE2E2' : transcriptionInfo.isSilent ? '#F3F4F6' : '#E0F2FE',
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            justifyContent: 'center',
                                                            border: '1px solid',
                                                            borderColor: transcriptionInfo.isError ? '#FECACA' : transcriptionInfo.isSilent ? '#D1D5DB' : '#BAE6FD',
                                                        }}>
                                                            {transcriptionInfo.isSilent ? (
                                                                <Box component="span" sx={{ fontSize: 9, color: '#6B7280', fontWeight: 600, lineHeight: 1 }}>∅</Box>
                                                            ) : (
                                                                <FormatQuoteIcon sx={{ 
                                                                    fontSize: 9, 
                                                                    color: transcriptionInfo.isError ? '#DC2626' : '#0284C7',
                                                                }} />
                                                            )}
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </ListItemIcon>
                                        <ListItemText
                                            primaryTypographyProps={{
                                                fontSize: 13,
                                                fontWeight: 400,
                                                noWrap: true,
                                            }}
                                            primary={primaryNode}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </PaperPanel>

                {/* Unsupported Files Dialog */}
                <UnsupportedFilesDialog
                    isOpen={unsupportedFilesDialogOpen}
                    onClose={() => {
                        setUnsupportedFilesDialogOpen(false);
                        setUnsupportedFiles([]);
                        setProcessableFiles([]);
                        processableRawFilesRef.current = [];
                    }}
                    files={unsupportedFiles}
                    processableFiles={processableFiles}
	                    onUploadProcessable={async () => {
	                        const projectId = ProjectAssetsStore.projectId as string;
	                        if (!projectId) return;

                        const toUpload = [...(processableRawFilesRef.current ?? [])];
                        setUnsupportedFilesDialogOpen(false);
                        setUnsupportedFiles([]);
                        setProcessableFiles([]);
                        processableRawFilesRef.current = [];

	                        for (const file of toUpload) {
	                            await UploadNonLocalFirstAsset({
	                                projectId,
	                                projectType: LibrettoProjectTypes.ProjectTypeEditor,
	                                file,
	                                filetype: "video",
	                                languageCode: "auto",
	                            });
	                        }
	                    }}
	                />
            </Box>
        </ClickAwayListener>
    );
});
